/**
 * What the Armadillo conversation's composer says when it cannot send, and
 * how a refused reply is described. Kept apart from the components so the
 * words are one import away for any surface that needs them.
 */

import { RegistryOrderError } from '@/services/registry-order-error';

/** What is said when the API has no send token here. The API's own
 * sentence says the same; this one stands in when a body carried none. */
export const REPLY_NOT_CONFIGURED_MESSAGE =
  'Replying from GTI Hub is not set up here - answer from the mailbox';

/** What the composer says when nothing can be answered. */
export const NOTHING_TO_ANSWER =
  'Nothing from Armadillo to answer yet. Replies answer a message from the research desk; to start a new email, use the mailbox.';

export interface ReplyFailure {
  title: string;
  message: string;
  /** The environment cannot send: no retry will help until it is set up. */
  final: boolean;
}

/** A quote asked for with the reply but not filled in: nothing is sent. */
export const QUOTE_INCOMPLETE: ReplyFailure = {
  title: 'The quote is not complete',
  message: 'Enter the price, the currency and the turnaround, or untick the quote.',
  final: false,
};

/** How a refused reply reads: a 503 is final (this environment cannot
 * send), anything else is the server's sentence and may be tried again. */
export function describeReplyFailure(err: unknown): ReplyFailure {
  if (err instanceof RegistryOrderError && err.kind === 'reply_not_configured') {
    const message = err.message.startsWith('Error ') ? REPLY_NOT_CONFIGURED_MESSAGE : err.message;
    return { title: 'Replying is not set up here', message, final: true };
  }
  const message = err instanceof Error ? err.message : 'The request failed';
  return { title: 'The reply was not sent', message, final: false };
}
